import React, { useEffect, useState, useContext } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../contexts/AuthContext';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import WriteReviewModal from './WriteReviewModal';
import {
  FONT_HEADLINE,
  FONT_SUBHEADLINE,
  FONT_BODY,
  FONT_FOOTNOTE,
  FONT_CAPTION,
  FONT_CAPTION2,
} from '../constants/typography';
import {
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
} from '../constants/layout';
import { AUTH_ACTION_LABEL } from '../constants/settings';

const formatDate = timestamp => {
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

function StarRating({ rating, size = 14 }) {
  return (
    <span className="pr-stars" style={{ fontSize: size }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span
          key={star}
          className={star <= rating ? 'pr-star-filled' : 'pr-star-empty'}
        >
          ★
        </span>
      ))}
    </span>
  );
}

function StarRatingInput({ rating, onChange, size = 32 }) {
  const [hovered, setHovered] = useState(0);

  return (
    <span className="pr-star-input" style={{ fontSize: size }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span
          key={star}
          className={
            star <= (hovered || rating) ? 'pr-star-filled' : 'pr-star-empty'
          }
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          style={{ cursor: 'pointer' }}
        >
          ★
        </span>
      ))}
    </span>
  );
}

function RatingBreakdownChart({ reviews }) {
  const total = reviews.length;
  const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach(r => {
    breakdown[r.rating] = (breakdown[r.rating] || 0) + 1;
  });

  return (
    <div className="pr-breakdown">
      {[5, 4, 3, 2, 1].map(star => {
        const count = breakdown[star] || 0;
        const fraction = total > 0 ? count / total : 0;
        return (
          <div key={star} className="pr-breakdown-row">
            <span className="pr-breakdown-label">{star}</span>
            <span className="pr-breakdown-star">★</span>
            <div className="pr-breakdown-bar-bg">
              <div
                className="pr-breakdown-bar-fill"
                style={{ width: `${fraction * 100}%` }}
              />
            </div>
            <span className="pr-breakdown-count">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function ReviewCard({ review }) {
  return (
    <div className="pr-review-card">
      <div className="pr-review-header">
        <StarRating rating={review.rating} size={13} />
        <span className="pr-review-date">{formatDate(review.createdAt)}</span>
      </div>

      {review.title && <div className="pr-review-title">{review.title}</div>}
      {review.body && <div className="pr-review-body">{review.body}</div>}

      {review.imageURLs && review.imageURLs.length > 0 && (
        <div className="pr-review-images">
          {review.imageURLs.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Review photo ${i + 1}`}
              className="pr-review-thumb"
            />
          ))}
        </div>
      )}

      <div className="pr-review-verified">
        <span className="pr-verified-icon">✓</span>
        <span>Verified Purchase</span>
        <span className="pr-dot">·</span>
        <span>{review.userFirstName}</span>
      </div>

      {review.merchantResponse && (
        <div className="pr-owner-response">
          <div className="pr-owner-response-header">
            <strong>Owner Response</strong>
            {review.merchantResponseDate && (
              <>
                <span className="pr-dot">·</span>
                <span>{formatDate(review.merchantResponseDate)}</span>
              </>
            )}
          </div>
          <div className="pr-owner-response-body">
            {review.merchantResponse}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductReviewsSection({ productId }) {
  const { user } = useContext(AuthContext);
  const { showReviews } = useDisplayPreferences();
  const [reviews, setReviews] = useState([]);
  const [hasPurchased, setHasPurchased] = useState(null);
  const [showWriteModal, setShowWriteModal] = useState(false);

  // Real-time reviews listener
  useEffect(() => {
    if (!productId) return;
    const q = query(
      collection(db, 'reviews'),
      where('productID', '==', productId)
    );
    const unsub = onSnapshot(q, snapshot => {
      const parsed = snapshot.docs
        .map(doc => doc.data())
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      setReviews(parsed);
    });
    return () => unsub();
  }, [productId]);

  // Check purchase eligibility lazily
  useEffect(() => {
    if (!user || !productId) {
      setHasPurchased(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ordersQuery = query(
          collection(db, 'orders'),
          where('userID', '==', user.uid)
        );
        const snapshot = await getDocs(ordersQuery);
        let found = false;
        snapshot.docs.forEach(doc => {
          const items = doc.data().items || [];
          if (items.some(item => item.productID === productId)) {
            found = true;
          }
        });
        if (!cancelled) setHasPurchased(found);
      } catch (err) {
        console.error('Error checking purchase history:', err);
        if (!cancelled) setHasPurchased(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, productId]);

  if (!showReviews) return null;

  const average =
    reviews.length > 0
      ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
      : 0;

  const hasAlreadyReviewed =
    user && reviews.some(r => r.userID === user.uid);

  const hasContent = reviews.some(
    r => r.title || r.body || (r.imageURLs && r.imageURLs.length > 0)
  );

  return (
    <div className="pr-section">
      <h3 className="pr-section-title">Customer Reviews</h3>

      {reviews.length > 0 ? (
        <>
          {/* Rating summary */}
          <div className="pr-summary">
            <div className="pr-summary-left">
              <div className="pr-average">{average.toFixed(1)}</div>
              <StarRating rating={Math.round(average)} size={14} />
              <div className="pr-review-count">
                {reviews.length} review{reviews.length === 1 ? '' : 's'}
              </div>
            </div>
            <RatingBreakdownChart reviews={reviews} />
          </div>

          {/* Individual reviews */}
          {hasContent && (
            <div className="pr-reviews-list">
              {reviews.slice(0, 3).map(review => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="pr-empty">No reviews yet. Be the first!</p>
      )}

      {/* Write a review section */}
      <div className="pr-write-section">
        {!user ? (
          <p className="pr-write-message">{AUTH_ACTION_LABEL} to write a review</p>
        ) : hasAlreadyReviewed ? (
          <p className="pr-write-message">
            You've already reviewed this product
          </p>
        ) : hasPurchased === null ? (
          <p className="pr-write-message">Checking purchase history...</p>
        ) : !hasPurchased ? (
          <p className="pr-write-message">
            Purchase this product to leave a review
          </p>
        ) : (
          <button
            className="pr-write-button"
            onClick={() => setShowWriteModal(true)}
          >
            Write a Review
          </button>
        )}
      </div>

      {showWriteModal && (
        <WriteReviewModal
          productId={productId}
          user={user}
          onClose={() => setShowWriteModal(false)}
        />
      )}

      <style>{`
        .pr-section {
          padding: ${SPACING_CARD_BODY} 0;
        }
        .pr-section-title {
          font-size: ${FONT_HEADLINE};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 ${SPACING_BUTTON} 0;
        }

        /* Summary */
        .pr-summary {
          display: flex;
          align-items: flex-start;
          gap: ${SPACING_CARD};
          margin-bottom: ${SPACING_BUTTON};
        }
        .pr-summary-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 80px;
          gap: 4px;
        }
        .pr-average {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
          line-height: 1;
          font-variant-numeric: tabular-nums;
        }
        .pr-review-count {
          font-size: ${FONT_CAPTION};
          color: var(--color-text-secondary);
        }

        /* Stars */
        .pr-stars {
          display: inline-flex;
          gap: 1px;
        }
        .pr-star-filled {
          color: #f5c518;
        }
        .pr-star-empty {
          color: var(--color-text-secondary);
          opacity: 0.25;
        }
        .pr-star-input {
          display: inline-flex;
          gap: 4px;
          user-select: none;
        }

        /* Breakdown chart */
        .pr-breakdown {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .pr-breakdown-row {
          display: flex;
          align-items: center;
          gap: ${SPACING_TIGHT};
        }
        .pr-breakdown-label {
          font-size: ${FONT_CAPTION2};
          color: var(--color-text-secondary);
          width: 12px;
          text-align: right;
        }
        .pr-breakdown-star {
          font-size: 8px;
          color: var(--color-text-secondary);
        }
        .pr-breakdown-bar-bg {
          flex: 1;
          height: ${SPACING_ELEMENT};
          background: var(--color-border, #e5e5e5);
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
        }
        .pr-breakdown-bar-fill {
          height: 100%;
          background: #f5c518;
          border-radius: ${BORDER_RADIUS};
          transition: width 0.3s ease;
        }
        .pr-breakdown-count {
          font-size: ${FONT_CAPTION2};
          color: var(--color-text-secondary);
          width: 20px;
        }

        /* Review cards */
        .pr-reviews-list {
          border-top: 1px solid var(--color-border, #e5e5e5);
          padding-top: ${SPACING_COMPONENT};
        }
        .pr-review-card {
          padding: ${SPACING_COMPONENT} 0;
          border-bottom: 1px solid var(--color-border, #e5e5e5);
        }
        .pr-review-card:last-child {
          border-bottom: none;
        }
        .pr-review-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${SPACING_TIGHT};
        }
        .pr-review-date {
          font-size: ${FONT_CAPTION};
          color: var(--color-text-secondary);
        }
        .pr-review-title {
          font-size: ${FONT_SUBHEADLINE};
          font-weight: 600;
          color: var(--color-text-primary);
          margin-bottom: 4px;
        }
        .pr-review-body {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-primary);
          line-height: 1.5;
          margin-bottom: ${SPACING_ELEMENT};
        }
        .pr-review-images {
          display: flex;
          gap: ${SPACING_ELEMENT};
          overflow-x: auto;
          padding-bottom: 4px;
          margin-bottom: ${SPACING_ELEMENT};
        }
        .pr-review-thumb {
          width: 64px;
          height: 64px;
          object-fit: cover;
          border-radius: ${BORDER_RADIUS};
          flex-shrink: 0;
        }
        .pr-review-verified {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: ${FONT_CAPTION2};
          color: var(--color-text-secondary);
        }
        .pr-verified-icon {
          color: #22c55e;
          font-weight: 700;
        }
        .pr-dot {
          color: var(--color-text-secondary);
        }

        /* Owner response */
        .pr-owner-response {
          margin-top: ${SPACING_COMPACT};
          padding: ${SPACING_COMPACT};
          background: var(--color-hover, rgba(128,128,128,0.08));
          border-radius: ${BORDER_RADIUS};
        }
        .pr-owner-response-header {
          font-size: ${FONT_CAPTION};
          color: var(--color-text-primary);
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .pr-owner-response-body {
          font-size: ${FONT_CAPTION};
          color: var(--color-text-primary);
          line-height: 1.4;
        }

        /* Empty state */
        .pr-empty {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          margin: 0;
        }

        /* Write section */
        .pr-write-section {
          margin-top: ${SPACING_BUTTON};
          padding-top: ${SPACING_BUTTON};
          border-top: 1px solid var(--color-border, #e5e5e5);
        }
        .pr-write-message {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
          margin: 0;
        }
        .pr-write-button {
          width: 100%;
          padding: ${SPACING_COMPONENT};
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: white;
          background: var(--brand-primary, #7c3aed);
          border: none;
          border-radius: ${BORDER_RADIUS};
          cursor: pointer;
          transition: opacity 0.15s;
        }
        .pr-write-button:hover {
          opacity: 0.9;
        }

        @media (max-width: 480px) {
          .pr-summary {
            flex-direction: column;
            align-items: stretch;
          }
          .pr-summary-left {
            flex-direction: row;
            gap: ${SPACING_COMPONENT};
            min-width: unset;
          }
          .pr-average {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
}
